// Display helpers for the CRM inbox — pure, so they can be unit-tested and
// shared by the list, the conversation page and the dashboard.

import type { CrmConversationSource, CrmConversationStatus } from '../types';

export const SOURCE_LABEL: Record<CrmConversationSource, string> = {
  email: 'Email',
  form: 'Contact form',
  manual: 'Sent by us',
};

export const STATUS_LABEL: Record<CrmConversationStatus, string> = {
  open: 'Needs attention',
  waiting: 'Waiting on them',
  closed: 'Closed',
  spam: 'Spam',
};

export const STATUS_COLOR: Record<CrmConversationStatus, string> = {
  open: 'var(--scpa-primary)',
  waiting: 'var(--scpa-status-warning)',
  closed: 'var(--scpa-text-muted)',
  spam: 'var(--scpa-status-error)',
};

/** Who the thread is with, for a row: name if we have one, else the address. */
export function senderLabel(c: { fromName?: string | null; fromEmail: string }): string {
  return (c.fromName && c.fromName.trim()) || c.fromEmail;
}

/** "just now", "5m ago", "3h ago", "2d ago", then the date. */
export function relativeTime(iso: string | Date | null | undefined, now = Date.now()): string {
  if (!iso) return '';
  const t = typeof iso === 'string' ? new Date(iso).getTime() : iso.getTime();
  const diff = Math.max(0, now - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}

/** Hours → "45m", "3.2h", "1.5d". Null → "—". */
export function formatHours(h: number | null | undefined): string {
  if (h === null || h === undefined || !Number.isFinite(h)) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
  if (h < 48) return `${Math.round(h * 10) / 10}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
}

/** How long they have been waiting on us, with a tone that escalates:
 *  under 4h is fine, under a day is a nudge, over a day is overdue. */
export function waitingLabel(
  c: { needsReply: boolean; lastInboundAt?: string | null; status?: string },
  now = Date.now(),
): { text: string; tone: 'ok' | 'warn' | 'err' } | null {
  if (!c.needsReply || !c.lastInboundAt) return null;
  if (c.status === 'closed' || c.status === 'spam') return null;
  const hours = Math.max(0, (now - new Date(c.lastInboundAt).getTime()) / 36e5);
  const text = `Waiting ${formatHours(hours)}`;
  return { text, tone: hours >= 24 ? 'err' : hours >= 4 ? 'warn' : 'ok' };
}

export function formatMoney(n: number | null | undefined, currency = 'USD'): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${Math.round(n).toLocaleString()}`;
  }
}

export function formatPercent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** "a@x.com, b@y.com" ↔ array, for the To/Cc inputs. */
export function joinAddresses(list: string[] | undefined | null): string {
  return (list || []).join(', ');
}
